import { push } from 'redux-first-history';
import { all, takeEvery, put, call, select } from 'redux-saga/effects';
import ApiInstance from 'src/helpers/apiInstance';
import actions from './actions';
import { fieldValidations, resolvedErrorMessage, sendRequest } from '../../../../helpers/saga';
import { logger } from '../../../../helpers/logger';
import notifications from '../../../../redux/notifications/actions';
import { keyByProperty } from '../../../../helpers/core';
import { scrollToFirstError } from '../../../../helpers/scrollToFirstError';
import { isFinanceOrAdminOrSystemAdminRole } from '../../../../helpers/userRoles';
import { t } from '../../../../i18n';

const api = new ApiInstance('subunits/:id');
const sectorsApi = new ApiInstance('sectors/list?pageSize=9999');
const createSubUnitApi = new ApiInstance('subunits');
const subUnitsFormDataApi = new ApiInstance('subunits/form_data');
const administratorsListApi = new ApiInstance('administrators/list');
const unitsApi = new ApiInstance('units/list');

function* loadData({ payload }) {
  try {
    const { id, role, isEdit } = payload;
    const result = yield call(sendRequest, api.get, { pathParams: { id } }, notifications.REQUEST_ERROR);

    if (result.status === 200) {
      const { data } = result;

      yield put(
        actions.loadDataSuccess({
          data: { ...data, id },
        }),
      );

      if (isEdit) {
        const parentUnit = data.ancestors?.find(ancestor => ancestor.level === 'unit');
        const selectedUnit = parentUnit ? { id: parentUnit.id, name: parentUnit.name } : null;

        yield put(
          actions.edit({
            id,
            role,
            subUnit: {
              ...data,
              address: '',
              id,
              parent_id: parentUnit?.id || null,
            },
            selectedUnit,
          }),
        );
      }
    } else if (result?.status === 404) {
      yield put(push('/404'));
    }
  } catch (error) {
    logger.error(error);
  }
}

function* updateSubUnit(data) {
  try {
    const { payload } = data;
    const { editForm, id } = payload;

    const propsArray = Object.values(editForm);
    const subUnit = keyByProperty(propsArray, 'name', prop => prop.value);

    if (!subUnit.breaks_enabled) {
      subUnit.breaks_duration = 0;
      subUnit.breaks_compensation = false;
    }
    const { account_manager, activation_rep, sales_rep, state, ...otherProps } = subUnit;

    const result = yield call(
      sendRequest,
      api.put,
      {
        data: {
          subunit: {
            ...otherProps,
            account_manager_id: account_manager,
            activation_rep_id: activation_rep,
            sales_rep_id: sales_rep,
            country_id: subUnit.country,
            state_id: state,
          },
        },
        pathParams: { id },
      },
      notifications.REQUEST_ERROR,
    );

    if (result) {
      if (result.status === 200) {
        yield put(actions.saveSuccess());

        yield put(push(`/subUnits/${id}`));

        yield put(
          notifications.addNotification({
            headline: t('dash.common.notification.success_headline'),
            type: 'success',
            message: t('dash.common.notification.updated_successfully', {
              item: t('dash.subUnits.subUnit.unit_title'),
            }),
          }),
        );
      } else {
        const validations = fieldValidations(result);

        // A refused write (403 and the like) resolves without field validations; say so instead of marking fields.
        if (validations) {
          yield put(actions.saveError({ data: validations }));
        } else {
          yield put(notifications.requestError({ error: resolvedErrorMessage(result) }));
        }
      }
    }
  } catch (error) {
    logger.error(error);
  }
}

function* deleteSubUnit(data) {
  try {
    const { payload } = data;
    const { id } = payload;

    const result = yield call(sendRequest, api.delete, { pathParams: { id } }, notifications.REQUEST_ERROR);

    if (result?.status === 200) {
      yield put(actions.deleteSuccess());
      yield put(
        notifications.addNotification({
          headline: t('dash.common.notification.success_headline'),
          type: 'success',
          message: t('dash.common.notification.deleted_successfully', {
            item: t('dash.subUnits.subUnit.unit_title'),
          }),
        }),
      );
    }
  } catch (error) {
    logger.error(error);
  }
}

function* addSubUnit(data) {
  try {
    const { payload } = data;
    const { editForm } = payload;

    const propsArray = Object.values(editForm);
    const subUnit = keyByProperty(propsArray, 'name', prop => prop.value);
    const { state, ...otherProps } = subUnit;

    const result = yield call(
      sendRequest,
      createSubUnitApi.post,
      {
        data: {
          subunit: {
            ...otherProps,
            country_id: subUnit.country,
            state_id: state,
          },
        },
      },
      notifications.REQUEST_ERROR,
    );

    if (result) {
      if (result.status === 200) {
        const { id } = result.data;
        yield put(push(`/subUnits/${id}/managers/add`));
        yield put(
          notifications.addNotification({
            headline: t('dash.common.notification.success_headline'),
            type: 'success',
            message: t('dash.common.notification.created_successfully', {
              item: t('dash.subUnits.subUnit.unit_title'),
            }),
          }),
        );
      } else {
        const validations = fieldValidations(result);

        // A refused write (403 and the like) resolves without field validations; say so instead of marking fields.
        if (validations) {
          yield put(actions.saveError({ data: validations }));
        } else {
          yield put(notifications.requestError({ error: resolvedErrorMessage(result) }));
        }
      }
    }
  } catch (error) {
    logger.error(error);
  }
}

function* loadFormData({ payload }) {
  try {
    const { formDataResponse, sectorsResponse } = yield all({
      formDataResponse: call(sendRequest, subUnitsFormDataApi.get, {}, notifications.REQUEST_ERROR),
      sectorsResponse: call(sendRequest, sectorsApi.get, {}, notifications.REQUEST_ERROR),
    });

    if (formDataResponse?.status === 200 && sectorsResponse?.status === 200) {
      const formData = formDataResponse.data;
      const sectors = sectorsResponse.data.records;

      yield put(actions.loadFormDataSuccess({ data: { ...formData, sectors } }));

      if (payload.id === 'add') {
        yield put(actions.create({ role: payload.role }));
      }
    }
  } catch (error) {
    logger.error(error);
  }
}

function* loadManagersFormData({ payload }) {
  try {
    const isAcceptedRole = isFinanceOrAdminOrSystemAdminRole(payload.role);
    const result = isAcceptedRole
      ? yield call(sendRequest, administratorsListApi.get, { search: { pageSize: 2000 } }, notifications.REQUEST_ERROR)
      : null;

    if (result) {
      const { data } = result;

      yield put(
        actions.loadManagersFormData({
          data,
        }),
      );
    }
  } catch (error) {
    logger.error(error);
  }
}

function* filterByLegalEntity(data) {
  const isLegalEntityEnabled = yield select(state =>
    Boolean(state.StartAppSettings?.feature_flags?.legal_entity_enabled),
  );

  if (!isLegalEntityEnabled) return data;

  const legalEntityId = yield select(
    state => state.SubUnit.subUnit?.legal_entity?.id || state.SubUnit.editForm?.legal_entity_id?.value,
  );

  if (!legalEntityId) return data;

  if (Array.isArray(data)) return data.filter(item => item.legal_entity_id === legalEntityId);

  if (Array.isArray(data?.data)) {
    return { ...data, data: data.data.filter(item => item.legal_entity_id === legalEntityId) };
  }

  return data;
}

function* filterUnits({ payload }) {
  try {
    const { filter } = payload;
    let result = null;

    if (filter) {
      result = yield call(
        sendRequest,
        unitsApi.get,
        { search: { filter, pageSize: 100, column: 'name' } },
        notifications.REQUEST_ERROR,
      );
    }

    if (result?.status === 200 && result?.data) {
      const filtered = yield call(filterByLegalEntity, result.data);

      yield put(actions.filterUnitsSuccess({ data: filtered }));
    } else if (!filter) {
      yield put(actions.filterUnitsSuccess({ data: [] }));
    }
  } catch (error) {
    logger.error(error);
  }
}

export { loadData };
export { loadFormData };
export { addSubUnit };
export { updateSubUnit };
export { deleteSubUnit };
export { loadManagersFormData };
export { filterUnits };
export default function* rootSaga() {
  yield all([
    takeEvery(actions.LOAD, loadData),
    takeEvery(actions.SAVE, updateSubUnit),
    takeEvery(actions.DELETE, deleteSubUnit),
    takeEvery(actions.ADD, addSubUnit),
    takeEvery(actions.EDIT, loadFormData),
    takeEvery(actions.LOAD_FORM_DATA, loadFormData),
    takeEvery(actions.EDIT, loadManagersFormData),
    takeEvery(actions.CREATE, loadManagersFormData),
    takeEvery(actions.FILTER_UNITS, filterUnits),
    takeEvery(actions.SAVE_ERROR, scrollToFirstError),
  ]);
}
